import {
  COMPETITION_RESPONSE_OPTIONS,
  CURRENT_STATE_OPTIONS,
  EXERCISE_FAMILIARITY_OPTIONS,
} from "./assessmentData";

/**
 * Profile-aware physiology education.
 *
 * These modules explain why an already-assigned profile receives its plan.
 * They never participate in scoring and never infer biomarker concentrations
 * from self-reported answers.
 */

export const EDUCATIONAL_MODULE_ORDER = [
  "neuroplasticity",
  "bdnf",
  "exercise-modes",
  "competition",
];

const SCIENCE_FOUNDATION = {
  neuroplasticity: {
    title: "Neuroplasticity — Why Your Current Pattern Can Change",
    science:
      "Your current response pattern is not fixed. Neuroplasticity is the central nervous system's ability to strengthen, weaken, and reorganize connections through repeated experience. When you repeatedly notice an earlier cue, use a manageable response, and reach a useful recovery point, the brain receives another opportunity to make that route easier to access next time.",
    biology:
      "This learning involves communication among sensory, motor, attention, memory, and autonomic networks. Repetition alone is not the goal: the experience needs to be receivable enough for you to complete it and recover. Movement changes circulation and sensory input, while sleep and recovery help consolidate learning. Dopamine contributes to effort and reinforcement, but no single chemical explains the whole change.",
    sources: [
      "Erickson et al. 2011 — aerobic training, hippocampal volume, and memory (PMID 21282661)",
      "Pessiglione et al. 2006 — dopamine-dependent reward prediction in humans (PMID 16929307)",
      "Treadway et al. 2012 — dopamine and effort-based decisions in humans (PMID 22553023)",
    ],
  },
  bdnf: {
    title: "BDNF — One Part of How Your Brain Learns and Adapts",
    science:
      "BDNF stands for brain-derived neurotrophic factor. It is a protein involved in neuronal health, synaptic plasticity, learning, and memory. This matters to you because repeated, recoverable experiences can help the brain update familiar response routes; BDNF is one part of the biological environment that supports that process, not a single explanation for sadness, withdrawal, anxiety, or mental fog.",
    biology:
      "Physical activity can temporarily change circulating BDNF, although the response varies by person, activity, intensity, timing, and measurement method. Movement also changes blood flow, energy use, sensory input, and autonomic activity. Sleep, nourishment, recovery, and repetition influence whether an experience can be learned from, which is why the most useful dose is one you can perform with control and recover from—not simply the hardest dose possible.",
    sources: [
      "Ferris, Williams & Shen 2007 — acute exercise, serum BDNF, and cognition (PMID 17414812)",
      "Griffin et al. 2011 — aerobic exercise, hippocampal function, and serum BDNF (PMID 21722657)",
      "Yang et al. 2020 — BDNF, neural plasticity, and mood-related research (PMID 32351365)",
    ],
  },
  "exercise-modes": {
    title: "Different Types of Exercise — Why the Right Type and Amount Matter",
    science:
      "Walking, cycling, and other steady activities create rhythm and sustained effort. Strength exercise practises force and control. Short hard intervals create a much larger demand. Each type gives your nervous system a different job, so the best choice depends on your current state and ability.",
    biology:
      "Exercise changes circulation, muscle recruitment, attention, and the chemicals that help your body produce energy. Moderate aerobic activity can affect endocannabinoid signalling. Harder work increases lactate and the energy-mobilizing messengers epinephrine and norepinephrine. A bigger disruption requires more recovery, so the plan matches the work to what you can handle now.",
    sources: [
      "Raichlen et al. 2013 — exercise intensity and endocannabinoid signalling (PMID 22990628)",
      "Nuzzo et al. 2016 — acute strength training and motor-cortical responses (PMID 26258855)",
      "Meyer et al. 1996 — interval exercise and catecholamine responses (PMID 8809522)",
      "Parekh & Lee 2005 — exercise intensity and post-exercise autonomic recovery (PMID 15809558)",
    ],
  },
  competition: {
    title:
      "Competition and Play — How Engagement Can Change Your Energy and Focus",
    science:
      "A game, score, partner, or skill target can make movement more interesting and give your attention a clear job. For some people this improves effort and focus. For others it adds pressure. Your answer helps the plan choose competition, cooperative play, or a non-competitive option.",
    biology:
      "Competition can raise activation before the activity even starts, including changes in stress responses involving cortisol. That can be useful when it helps you engage and stay in control, but unhelpful when it makes it harder to settle afterward. The useful test is how you respond, not whether competition is always good or bad.",
    sources: [
      "DiMenichi & Tricomi 2015 — competition, physical effort, and attention (PMCID PMC4554955)",
      "Cook & Crewther 2019 — competitive context, cortisol, and emotional response (PMID 30471383)",
      "Treadway et al. 2012 — dopamine and effort-based decisions in humans (PMID 22553023)",
    ],
  },
};

const PROFILE_EMPHASIS = {
  "high-activation": {
    neuroplasticity: {
      relevance:
        "Your result emphasizes replacing the activation-to-immediate-relief route with controlled movement, a deliberate downshift, and observed feedback. Repetition is used to rehearse an alternative route through the Dependency and Adaptive Loops.",
      homeostasis:
        "The aim is not to eliminate activation. It is to practise moving from mobilization back toward a workable range so the central nervous system receives repeated evidence that effort can end in recovery.",
      planLink:
        "This is why your plan pairs every purposeful effort with a visible reduction in pace and a second emotional-intensity check.",
    },
    bdnf: {
      relevance:
        "For this profile, BDNF is relevant as part of the learning environment created by repeatable exercise—not as a reason to add more arousal to an already activated state.",
      homeostasis:
        "A recoverable dose protects the return toward homeostasis. Chasing a larger acute biological response can be counterproductive when activation and recovery scores are already strained.",
      planLink:
        "Your plan therefore prioritizes repeatable controlled work; vigorous work appears only when readiness, familiarity, hydration, nourishment, and control support it.",
    },
    "exercise-modes": {
      relevance:
        "Rhythmic aerobic work or familiar resistance exercise can organize mobilized energy. Anaerobic work can be useful only as a brief, controlled outlet when it is familiar and your current readiness supports it.",
      homeostasis:
        "Higher-intensity work produces a larger sympathetic and metabolic disturbance, so the recovery phase is part of the intervention rather than an optional finish.",
      planLink:
        "The mode is selected by current ability and familiarity; the required endpoint is controlled recovery, not exhaustion.",
    },
    competition: {
      relevance:
        "Competition is useful only when it focuses your energy without turning the session into another uncontrolled stressor. Cooperative games, skill targets, or time-limited challenges can provide engagement with less social pressure.",
      homeostasis:
        "Because competition can raise arousal before movement even begins, the relevant test is whether you can stay in control and settle after the activity.",
      planLink:
        "Your competition answer determines whether the plan uses friendly challenge, social movement without scoring, or a solo format.",
    },
  },
  "reduced-capacity": {
    neuroplasticity: {
      relevance:
        "Your result emphasizes small successful repetitions. Beginning, completing, and recovering from a manageable input provides stronger learning value than an ambitious session that cannot be repeated.",
      homeostasis:
        "The homeostatic challenge should be small enough that energy, pain, and motivation return to a workable range instead of remaining disrupted.",
      planLink:
        "This is why your week uses short, accessible movement to build initiation, confidence, and a repeatable completion signal.",
    },
    bdnf: {
      relevance:
        "BDNF-related exercise research supports movement as part of a learning environment, but this profile does not need to chase the highest intensity to make the input biologically meaningful.",
      homeostasis:
        "Frequency and recovery matter because a dose that repeatedly overshoots current capacity cannot provide a stable adaptation signal.",
      planLink:
        "Your plan starts with light-to-moderate work that leaves enough energy to repeat the input across the week.",
    },
    "exercise-modes": {
      relevance:
        "Accessible aerobic movement and foundational resistance work provide the best starting range: rhythm, circulation, motor recruitment, competence, and visible progress without requiring maximal output.",
      homeostasis:
        "Anaerobic intervals create the largest immediate disturbance of the three modes, so they are a later progression rather than the default starting point for reduced capacity.",
      planLink:
        "The plan uses the familiar activity you can perform most consistently and keeps the first progression to one variable at a time.",
    },
    competition: {
      relevance:
        "Low-pressure participation can add enjoyment, structure, and connection. Winning is not required; shared movement can be valuable even when competition itself is not motivating.",
      homeostasis:
        "A supportive format should raise engagement without creating a stress response that makes initiation or recovery harder.",
      planLink:
        "Your competition answer determines whether the plan uses a simple shared goal, cooperative activity, or no competitive element.",
    },
  },
  "recovery-focus": {
    neuroplasticity: {
      relevance:
        "Your result emphasizes learning the transition out of demand. The repeated skill is not producing more effort; it is recognizing the end of effort and reliably completing the recovery side of the Adaptive Loop.",
      homeostasis:
        "The nervous system needs enough lower-demand time for heart rate, breathing, muscular tension, attention, and energy availability to move back toward their working ranges.",
      planLink:
        "This is why the week places an active recovery anchor before strain usually accumulates rather than adding another hard training target.",
    },
    bdnf: {
      relevance:
        "BDNF is relevant to exercise-supported learning, but an acute BDNF response does not make an additional hard session the right choice when recovery is already trailing load.",
      homeostasis:
        "Consolidation requires the biological response to exercise and the recovery resources that follow it. More stimulus without recovery can increase cost without improving the next response.",
      planLink:
        "Your plan uses submaximal rhythmic activity and predictable recovery to improve the return between existing demands.",
    },
    "exercise-modes": {
      relevance:
        "Easy aerobic work and controlled mobility create a recovery-oriented rhythm. Resistance work can remain when volume and effort are managed; anaerobic work should not become another accumulated demand.",
      homeostasis:
        "The selected mode should leave autonomic recovery moving in the right direction. The plan therefore values the settled finish as much as the work itself.",
      planLink:
        "Familiarity helps select a low-friction mode, while the dose is capped by the goal of feeling more recovered afterward.",
    },
    competition: {
      relevance:
        "Competition can increase engagement, but it can also turn recovery movement into another performance demand. Low-pressure social activity is often the better fit when the recovery score is the limiting factor.",
      homeostasis:
        "The useful format is one that supports a return toward baseline instead of extending cortisol, catecholamine, and attentional demand.",
      planLink:
        "Your competition answer determines whether the week includes friendly play, social movement without scoring, or a quiet solo recovery format.",
    },
  },
  "regulation-building": {
    neuroplasticity: {
      relevance:
        "Your result emphasizes consistency under ordinary stress. Repeating the same useful input near the same pressure window helps the nervous system connect an early state cue with an adaptive response.",
      homeostasis:
        "The goal is a more reliable return toward the working range after manageable demand, not the absence of physiological change.",
      planLink:
        "This is why the week repeats a moderate input and changes only one variable—time, pace, resistance, or complexity.",
    },
    bdnf: {
      relevance:
        "A consistent mix of aerobic, resistance, and skill practice can provide repeated learning-related signals. For this profile, consistency is more useful than searching for one supposedly optimal BDNF workout.",
      homeostasis:
        "A moderate challenge followed by recovery creates a repeatable disturbance-and-return cycle that can be progressed as regulation becomes more automatic.",
      planLink:
        "Your plan uses a dose you can repeat and then advances one feature only when the previous dose remains controlled.",
    },
    "exercise-modes": {
      relevance:
        "Moderate aerobic work builds rhythm and sustained effort; resistance training adds force and motor learning; controlled intervals add a stronger effort-to-recovery transition when they are familiar.",
      homeostasis:
        "Mixed training broadens the kinds of demand the system can receive, while deliberate recovery prevents variety from becoming accumulated load.",
      planLink:
        "Your familiar modes determine which option has the lowest friction and which can be introduced as the next progression.",
    },
    competition: {
      relevance:
        "Friendly competition can make practice more engaging and provide immediate feedback when it helps you focus. If it creates pressure, cooperative games or shared skill practice preserve the social input without the same evaluative load.",
      homeostasis:
        "The adaptive target is flexible arousal: recruit enough activation for the task, then return toward baseline after the result is known.",
      planLink:
        "Your answer selects between friendly competition, cooperative participation, or a non-competitive format.",
    },
  },
  "growth-performance": {
    neuroplasticity: {
      relevance:
        "Your result supports progressive challenge, complex skill, and performance feedback. Neuroplasticity is relevant to refining movement, attention, decisions, and recovery under higher but controlled demand.",
      homeostasis:
        "A higher-capacity system can tolerate a larger disruption, but it still needs a complete return cycle. Progress depends on challenge and recovery remaining matched as the ceiling rises.",
      planLink:
        "This is why your plan progresses a defined performance variable while protecting the recovery conditions that keep learning quality high.",
    },
    bdnf: {
      relevance:
        "Aerobic work, intervals, and complex skill practice can contribute to an exercise environment associated with BDNF and learning. The performance value comes from the complete training process, not a single molecule or maximal session.",
      homeostasis:
        "Greater training stimulus can create a larger biological response and a larger recovery obligation. Deloads, sleep, nutrition, and spacing preserve the capacity to adapt again.",
      planLink:
        "Your plan uses progressive moderate-to-vigorous work while preventing intensity, volume, and complexity from rising together.",
    },
    "exercise-modes": {
      relevance:
        "Aerobic training develops sustained output, resistance training develops force and neural drive, and anaerobic intervals develop rapid energy delivery and recovery between hard efforts. A mixed program can target all three deliberately.",
      homeostasis:
        "Performance training intentionally creates larger departures from baseline; readiness, session spacing, and recovery determine whether that disturbance becomes adaptation or accumulated fatigue.",
      planLink:
        "Your familiar activities and performance goal determine the mode; the week still defines recovery before the hard exposure begins.",
    },
    competition: {
      relevance:
        "Competition can add consequence, decision speed, social feedback, and meaningful performance pressure. It is useful when it improves engagement and execution rather than narrowing attention beyond control.",
      homeostasis:
        "Competition often adds biological load before and after the physical work. Recovery must account for the full event—the anticipation, effort, outcome, and return—not only the exercise minutes.",
      planLink:
        "Your competition answer determines whether performance pressure is used as a training input, bounded more tightly, or replaced with self-referenced targets.",
    },
  },
};

const score = (value) => (Number.isFinite(value) ? value.toFixed(1) : "3.0");

function getScoreContext(profileKey, dimensions = {}, behaviors = []) {
  const state = score(dimensions.state);
  const inputs = score(dimensions.inputs);
  const capacity = score(dimensions.capacity);
  const patternCount = Array.isArray(behaviors)
    ? behaviors.filter((item) =>
        [
          "withdrawal",
          "avoidance",
          "scrolling",
          "substance",
          "eating",
          "overwork",
        ].includes(item),
      ).length
    : 0;

  switch (profileKey) {
    case "high-activation":
      return `Your Current State score is ${state}/5 and you reported ${patternCount} recurring stress-response pattern${patternCount === 1 ? "" : "s"}. That combination is why these modules emphasize activation, reinforcement, and recovery.`;
    case "reduced-capacity":
      return Number(dimensions.capacity) <= 2.5
        ? `Your Physical Capacity score is ${capacity}/5. That is why these modules prioritize a receivable dose before greater intensity.`
        : `Your Current State (${state}/5) and Current Inputs (${inputs}/5) combine to indicate reduced available capacity. That is why the science is applied through smaller, repeatable inputs.`;
    case "recovery-focus":
      return `Your Current State score (${state}/5) is lower than your Inputs (${inputs}/5) and Capacity (${capacity}/5). That gap is why these modules emphasize consolidation and the return after effort.`;
    case "growth-performance":
      return `Your State (${state}/5), Inputs (${inputs}/5), and Capacity (${capacity}/5) scores are all in the higher range. That is why these modules emphasize progressive challenge while preserving recovery.`;
    default:
      return `Your State (${state}/5), Inputs (${inputs}/5), and Capacity (${capacity}/5) sit in a working range. That is why these modules emphasize consistency, controlled progression, and a more reliable recovery response.`;
  }
}

function labelFor(options, key, fallback) {
  return options.find((option) => option.key === key)?.label || fallback;
}

function getCurrentStateContext(answers = {}) {
  const states = answers.current?.states || [];
  if (!states.length) return "No single current-state label was selected.";
  const labels = states.map((key) => labelFor(CURRENT_STATE_OPTIONS, key, key));
  return `Your current-state selection is ${labels.join(" and ").toLowerCase()}, so today's dose still needs to match the state you are actually in.`;
}

function getFamiliarityContext(answers = {}) {
  const selected = answers.schedule?.familiarActivities || [];
  if (!selected.length || selected.includes("none-unsure")) {
    return "You did not identify a familiar training mode, so the result prioritizes simple movement or coached instruction before complex or vigorous work.";
  }
  const labels = selected.map((key) =>
    labelFor(EXERCISE_FAMILIARITY_OPTIONS, key, key),
  );
  return `You identified ${labels.join(", ").toLowerCase()} as familiar enough to perform with control; those modes receive priority over unfamiliar intensity.`;
}

function getCompetitionContext(answers = {}) {
  const key = answers.schedule?.competitionResponse;
  const label = labelFor(
    COMPETITION_RESPONSE_OPTIONS,
    key,
    "unsure or insufficient experience",
  );
  const application =
    {
      "focus-engage":
        "Competition can be used as a deliberate engagement input while the plan still protects control and recovery.",
      "motivates-overactivates":
        "Competition may help initiation but should be bounded by time, stakes, and recovery so activation does not outrun control.",
      "pressure-discouraging":
        "The result should preserve movement and social connection without scorekeeping, ranking, or unnecessary evaluation.",
      "little-effect":
        "Competition is optional; activity fit, competence, and social format matter more than winning or losing.",
      unsure:
        "A low-pressure experiment or cooperative format is more informative than assuming competition will regulate or motivate you.",
    }[key] ||
    "A low-pressure experiment or cooperative format is more informative than assuming competition will regulate or motivate you.";

  return `You reported that friendly competition ${label.toLowerCase()}. ${application}`;
}

export function getEducationalModules({
  profileKey,
  dimensions = {},
  answers = {},
} = {}) {
  const profile =
    PROFILE_EMPHASIS[profileKey] || PROFILE_EMPHASIS["regulation-building"];
  const scoreContext = getScoreContext(
    profileKey,
    dimensions,
    answers.behaviors,
  );
  const currentStateContext = getCurrentStateContext(answers);
  const familiarityContext = getFamiliarityContext(answers);
  const competitionContext = getCompetitionContext(answers);

  return EDUCATIONAL_MODULE_ORDER.map((id) => {
    const foundation = SCIENCE_FOUNDATION[id];
    const emphasis = profile[id];
    const implementationContext =
      id === "exercise-modes"
        ? familiarityContext
        : id === "competition"
          ? competitionContext
          : currentStateContext;

    return {
      id,
      title: foundation.title,
      scoreContext,
      science: foundation.science,
      biology: foundation.biology,
      profileRelevance: `${emphasis.relevance} ${implementationContext}`,
      homeostasis: emphasis.homeostasis,
      planLink: emphasis.planLink,
      bottomLine: emphasis.planLink,
      sources: foundation.sources,
    };
  });
}

export function getRobustEducationalModules({ profile } = {}) {
  const profileKey = profile?.legacyProfileKey || "regulation-building";
  const emphasis =
    PROFILE_EMPHASIS[profileKey] || PROFILE_EMPHASIS["regulation-building"];

  return ["neuroplasticity", "bdnf"].map((id) => {
    const foundation = SCIENCE_FOUNDATION[id];
    const profileEmphasis = emphasis[id];
    return {
      id,
      title: foundation.title,
      science: foundation.science,
      biology: foundation.biology,
      profileRelevance: profileEmphasis.relevance,
      homeostasis: profileEmphasis.homeostasis,
      bottomLine: profileEmphasis.planLink,
      sources: foundation.sources,
    };
  });
}

export const EDUCATIONAL_CLAIM_BOUNDARY =
  "The assessment uses self-reported scores to select relevant exercise-science mechanisms. It does not calculate dopamine, BDNF, cortisol, catecholamines, autonomic activity, or any other biomarker.";
